// ABOUTME: Modal for editing a support ticket label (short name, long name, color).
// ABOUTME: Wraps SHModal and reuses the shared ShColorPicker popover.

import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

import { tokens } from "../../../theme";
import { useUpdateSupportTicketLabelTypeMutation } from "../../../newapi/warehouse/supportTicketApiSlice";
import {
    showToastMessageUpdateRequest,
    showToastMessageRequestError,
} from "../../../utils/toasts";

import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import { ShColorPicker } from "../../ShColorPicker";

const DEFAULT_COLOR = "#6366F1";

const MEditServiceDeskLabelModal = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [shortName, setShortName] = useState("");
    const [longName, setLongName] = useState("");
    const [colorValue, setColorValue] = useState({ hex: DEFAULT_COLOR, opacity: 1 });
    const [pickerAnchor, setPickerAnchor] = useState(null);

    const [updateLabel, { isLoading }] = useUpdateSupportTicketLabelTypeMutation();

    useEffect(() => {
        if (props.open && props.label) {
            setShortName(props.label.shortName ?? "");
            setLongName(props.label.longName ?? "");
            setColorValue({ hex: props.label.color ?? DEFAULT_COLOR, opacity: 1 });
        }
    }, [props.open, props.label]);

    const save = async () => {
        if (!shortName || !longName) return;
        try {
            const result = await updateLabel({
                id: props.label.id,
                data: {
                    shortName,
                    longName,
                    color: colorValue.hex,
                },
            }).unwrap();
            showToastMessageUpdateRequest(t, result);
            props.handleClose();
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    let isStandAlone = false;
    if (window.matchMedia("(display-mode: standalone)").matches) {
        isStandAlone = true;
    }

    return (
        <SHModal open={props.open} onClose={props.handleClose}>
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Subheading>{t("edit")}</Subheading>
            </Box>

            <Box
                sx={{
                    background:
                        theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    pb: 2,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Box sx={{ width: 100 }}>
                        <Text>{t("short_name")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            background:
                                theme.palette.mode === "dark"
                                    ? colors.grey[300]
                                    : colors.grey[100],
                            borderRadius: 4,
                            height: 32,
                            display: "flex",
                        }}
                    >
                        <InputBase
                            value={shortName}
                            onChange={(e) => setShortName(e.target.value)}
                            sx={{ px: 2, flex: 1, color: colors.txt["primary"] }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Box sx={{ width: 100 }}>
                        <Text>{t("long_name")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            background:
                                theme.palette.mode === "dark"
                                    ? colors.grey[300]
                                    : colors.grey[100],
                            borderRadius: 4,
                            height: 32,
                            display: "flex",
                        }}
                    >
                        <InputBase
                            value={longName}
                            onChange={(e) => setLongName(e.target.value)}
                            sx={{ px: 2, flex: 1, color: colors.txt["primary"] }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Box sx={{ width: 100 }}>
                        <Text>{t("color")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <ButtonBase
                            onClick={(e) => setPickerAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 3,
                                background: colorValue.hex,
                                flexShrink: 0,
                                border: `1px solid ${colors.grey[200]}`,
                            }}
                        />
                        <Text>{colorValue.hex}</Text>
                    </Box>
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background:
                        theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    },
                }}
            >
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={props.handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        block
                        isLoading={isLoading}
                        variant="contained"
                        onClick={save}
                    >
                        {t("save")}
                    </Shbutton>
                </ButtonGroup>
            </Box>

            <ShColorPicker
                value={colorValue}
                setValue={setColorValue}
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
            />
        </SHModal>
    );
};

export default MEditServiceDeskLabelModal;
